import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../api/client';

// ══════════════════════════════════════════════════════════════════════════
// Types — mirror admin_brain.py response shapes + models/brain.py exactly
// ══════════════════════════════════════════════════════════════════════════
interface BrainQuestion {
  id: string;
  question_text_en: string;
  question_text_si: string | null;
  option_a_en: string; option_a_si: string | null;
  option_b_en: string; option_b_si: string | null;
  option_c_en: string; option_c_si: string | null;
  option_d_en: string; option_d_si: string | null;
  correct_option: string;
  explanation_en: string | null;
  explanation_si: string | null;
  section: string;
  category: string;
  sub_category: string | null;
  difficulty: string;
  language: string | null;
  linked_book_id: string | null;
  is_active: boolean;
  times_shown: number;
  times_correct: number;
  is_ai_generated: boolean;
  ai_reviewed: boolean;
  submitted_by_user_id: string | null;
  community_approved: boolean;
  created_at: string | null;
  updated_at: string | null;
}

interface QuestionListResponse { total: number; questions: BrainQuestion[]; }

interface BrainStats {
  total_questions: number;
  active_questions: number;
  by_section: { gk: number; iq: number };
  by_category: Record<string, number>;
  by_difficulty: Record<string, number>;
}

interface BulkImportResult { success_count: number; failed_count: number; errors: string[]; }

interface Toast { id: number; message: string; type: 'success' | 'error'; }

// ══════════════════════════════════════════════════════════════════════════
// Category constants — EXACT mirror of readora-backend/app/routers/brain.py
// GK_GENERAL_CATEGORIES / IQ_CATEGORIES + the 3 standalone gk categories.
// Do not add/remove values here without updating brain.py in lockstep —
// scoring buckets (gk_srilanka/gk_sinhala/gk_books/gk_general/iq_logic) are
// keyed off these exact strings server-side.
// ══════════════════════════════════════════════════════════════════════════
const GK_CATEGORIES: { value: string; label: string }[] = [
  { value: 'sri_lanka', label: 'Sri Lanka' },
  { value: 'sinhala_literature', label: 'Sinhala Literature' },
  { value: 'literature_and_authors', label: 'Literature & Authors' },
  { value: 'arts_culture', label: 'Arts & Culture' },
  { value: 'current_affairs', label: 'Current Affairs' },
  { value: 'geography', label: 'Geography' },
  { value: 'history', label: 'History' },
  { value: 'mathematics', label: 'Mathematics' },
  { value: 'science', label: 'Science' },
];

const IQ_CATEGORIES: { value: string; label: string }[] = [
  { value: 'abstract_reasoning', label: 'Abstract Reasoning' },
  { value: 'logical_reasoning', label: 'Logical Reasoning' },
  { value: 'memory_challenges', label: 'Memory Challenges' },
  { value: 'numerical_reasoning', label: 'Numerical Reasoning' },
  { value: 'processing_speed', label: 'Processing Speed' },
  { value: 'verbal_reasoning', label: 'Verbal Reasoning' },
];

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  [...GK_CATEGORIES, ...IQ_CATEGORIES].map((c) => [c.value, c.label])
);

const categoriesForSection = (section: string) =>
  section === 'iq' ? IQ_CATEGORIES : GK_CATEGORIES;

const PER_PAGE = 20;

const emptyForm = {
  section: 'gk',
  category: GK_CATEGORIES[0].value,
  sub_category: '',
  difficulty: 'medium',
  language: 'both',
  question_text_en: '',
  question_text_si: '',
  option_a_en: '', option_a_si: '',
  option_b_en: '', option_b_si: '',
  option_c_en: '', option_c_si: '',
  option_d_en: '', option_d_si: '',
  correct_option: 'a',
  explanation_en: '',
  explanation_si: '',
};

type FormState = typeof emptyForm;

// ══════════════════════════════════════════════════════════════════════════
// Small shared UI pieces (duplicated locally per existing project convention
// — see Trivia.tsx — no shared modal/toast library across admin pages)
// ══════════════════════════════════════════════════════════════════════════
const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border toast-enter cursor-pointer min-w-[280px] ${toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
      </div>
    ))}
  </div>
);

const ConfirmDialog: React.FC<{
  open: boolean; title: string; message: string; onConfirm: () => void; onCancel: () => void; loading: boolean;
}> = ({ open, title, message, onConfirm, onCancel, loading }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-slide-up">
        <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} disabled={loading} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className="px-4 py-2 bg-[#D85A30] hover:bg-[#D85A30]/90 text-white rounded-xl text-sm font-bold transition-colors disabled:opacity-50">
            {loading ? 'Processing...' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode; wide?: boolean }> = ({ isOpen, onClose, title, children, wide }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-3xl shadow-2xl w-full p-6 sm:p-8 animate-slide-up max-h-[90vh] overflow-y-auto ${wide ? 'max-w-3xl' : 'max-w-2xl'}`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 bg-gray-50 p-2 rounded-full transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const SectionBadge: React.FC<{ section: string }> = ({ section }) => (
  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${section === 'iq' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
    {section === 'iq' ? 'IQ' : 'GK'}
  </span>
);

const DifficultyBadge: React.FC<{ difficulty: string }> = ({ difficulty }) => {
  const styles: Record<string, string> = {
    easy: 'bg-green-50 text-green-700 border-green-200',
    medium: 'bg-amber-50 text-[#BA7517] border-amber-200',
    hard: 'bg-red-50 text-[#D85A30] border-red-200',
  };
  return (
    <span className={`px-2 py-1 rounded-md text-xs font-medium border capitalize ${styles[difficulty] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
      {difficulty}
    </span>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// Main component
// ══════════════════════════════════════════════════════════════════════════
const Brain: React.FC = () => {
  const [questions, setQuestions] = useState<BrainQuestion[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<BrainStats | null>(null);

  const [sectionFilter, setSectionFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [difficultyFilter, setDifficultyFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  // Add/Edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormState>(emptyForm);

  // Bulk import modal state
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkPreviewCount, setBulkPreviewCount] = useState<number | null>(null);
  const [bulkParseError, setBulkParseError] = useState<string | null>(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkImportResult | null>(null);

  // Delete confirm state
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string }>({ open: false, id: '' });
  const [isDeleting, setIsDeleting] = useState(false);

  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => { setToasts((prev) => prev.filter((t) => t.id !== id)); }, 4000);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminApi.get<BrainStats>('/admin/brain/stats');
      setStats(res.data);
    } catch {
      // Stats strip is non-critical — fail silently, table still works
    }
  }, []);

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { skip: (page - 1) * PER_PAGE, limit: PER_PAGE };
      if (sectionFilter !== 'All') params.section = sectionFilter;
      if (categoryFilter !== 'All') params.category = categoryFilter;
      if (difficultyFilter !== 'All') params.difficulty = difficultyFilter;
      if (statusFilter !== 'All') params.is_active = statusFilter === 'Active';

      const res = await adminApi.get<QuestionListResponse>('/admin/brain/questions', { params });
      setQuestions(res.data.questions || []);
      setTotal(res.data.total || 0);
    } catch (err: any) {
      addToast('Failed to load Brain questions.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, sectionFilter, categoryFilter, difficultyFilter, statusFilter, addToast]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const resetToPage1 = () => setPage(1);

  const openAddModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = (q: BrainQuestion) => {
    setEditingId(q.id);
    setFormData({
      section: q.section,
      category: q.category,
      sub_category: q.sub_category || '',
      difficulty: q.difficulty,
      language: q.language || 'both',
      question_text_en: q.question_text_en,
      question_text_si: q.question_text_si || '',
      option_a_en: q.option_a_en, option_a_si: q.option_a_si || '',
      option_b_en: q.option_b_en, option_b_si: q.option_b_si || '',
      option_c_en: q.option_c_en, option_c_si: q.option_c_si || '',
      option_d_en: q.option_d_en, option_d_si: q.option_d_si || '',
      correct_option: q.correct_option,
      explanation_en: q.explanation_en || '',
      explanation_si: q.explanation_si || '',
    });
    setIsModalOpen(true);
  };

  // Changing Section clears Category if it no longer belongs to that section
  // (prevents saving a gk category under section=iq or vice versa).
  const handleSectionChange = (section: string) => {
    const valid = categoriesForSection(section).some((c) => c.value === formData.category);
    setFormData((p) => ({ ...p, section, category: valid ? p.category : categoriesForSection(section)[0].value }));
  };

  // ── Submit — two DIFFERENT payload shapes on purpose ────────────────────
  // POST /admin/brain/questions (create) expects the short BulkImportItem
  // field names (q_en, a_en, correct, exp_en, ...).
  // PUT /admin/brain/questions/{id} (edit) takes a raw dict that is applied
  // via setattr() directly onto the SQLAlchemy model, so it must use the
  // actual model attribute names (question_text_en, option_a_en,
  // correct_option, explanation_en, ...). Sending the wrong key names here
  // would silently no-op (hasattr() check skips unknown keys) rather than
  // error, so both whitelists are spelled out explicitly — never spread
  // raw form state into either payload.
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (editingId) {
        const payload = {
          question_text_en: formData.question_text_en,
          question_text_si: formData.question_text_si || null,
          option_a_en: formData.option_a_en, option_a_si: formData.option_a_si || null,
          option_b_en: formData.option_b_en, option_b_si: formData.option_b_si || null,
          option_c_en: formData.option_c_en, option_c_si: formData.option_c_si || null,
          option_d_en: formData.option_d_en, option_d_si: formData.option_d_si || null,
          correct_option: formData.correct_option,
          explanation_en: formData.explanation_en || null,
          explanation_si: formData.explanation_si || null,
          section: formData.section,
          category: formData.category,
          sub_category: formData.sub_category || null,
          difficulty: formData.difficulty,
          language: formData.language,
        };
        await adminApi.put(`/admin/brain/questions/${editingId}`, payload);
        addToast('Question updated successfully.', 'success');
      } else {
        const payload = {
          q_en: formData.question_text_en,
          q_si: formData.question_text_si || null,
          a_en: formData.option_a_en, a_si: formData.option_a_si || null,
          b_en: formData.option_b_en, b_si: formData.option_b_si || null,
          c_en: formData.option_c_en, c_si: formData.option_c_si || null,
          d_en: formData.option_d_en, d_si: formData.option_d_si || null,
          correct: formData.correct_option,
          exp_en: formData.explanation_en || null,
          exp_si: formData.explanation_si || null,
          section: formData.section,
          category: formData.category,
          sub_category: formData.sub_category || null,
          difficulty: formData.difficulty,
          language: formData.language,
        };
        await adminApi.post('/admin/brain/questions', payload);
        addToast('Question added successfully.', 'success');
      }
      setIsModalOpen(false);
      fetchQuestions();
      fetchStats();
    } catch (err: any) {
      addToast(err.response?.data?.detail || 'Failed to save question.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggle = async (q: BrainQuestion) => {
    // Optimistic update, reverted on failure
    setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, is_active: !x.is_active } : x)));
    try {
      await adminApi.patch(`/admin/brain/questions/${q.id}/toggle`);
      fetchStats();
    } catch (err: any) {
      setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, is_active: q.is_active } : x)));
      addToast('Failed to update status.', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.id) return;
    try {
      setIsDeleting(true);
      await adminApi.delete(`/admin/brain/questions/${deleteConfirm.id}`);
      addToast('Question deactivated.', 'success');
      setDeleteConfirm({ open: false, id: '' });
      fetchQuestions();
      fetchStats();
    } catch (err: any) {
      addToast(err.response?.data?.detail || 'Failed to deactivate question.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Bulk import ───────────────────────────────────────────────────────
  const validateBulkText = (text: string) => {
    setBulkResult(null);
    if (!text.trim()) { setBulkPreviewCount(null); setBulkParseError(null); return; }
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        setBulkPreviewCount(null);
        setBulkParseError('JSON must be an array of question objects.');
        return;
      }
      const requiredKeys = ['q_en', 'a_en', 'b_en', 'c_en', 'd_en', 'correct', 'section', 'category'];
      const missingRow = parsed.findIndex((row: any) => requiredKeys.some((k) => !(k in row)));
      if (missingRow !== -1) {
        setBulkPreviewCount(null);
        setBulkParseError(`Row ${missingRow} is missing one of the required fields: ${requiredKeys.join(', ')}`);
        return;
      }
      setBulkParseError(null);
      setBulkPreviewCount(parsed.length);
    } catch (e: any) {
      setBulkPreviewCount(null);
      setBulkParseError('Invalid JSON: ' + e.message);
    }
  };

  const handleBulkSubmit = async () => {
    if (bulkPreviewCount === null || bulkParseError) return;
    setIsBulkSubmitting(true);
    setBulkResult(null);
    try {
      const parsed = JSON.parse(bulkText);
      const res = await adminApi.post<BulkImportResult>('/admin/brain/questions/bulk-import', parsed);
      setBulkResult(res.data);
      addToast(`Imported ${res.data.success_count} question(s).`, res.data.failed_count > 0 ? 'error' : 'success');
      fetchQuestions();
      fetchStats();
    } catch (err: any) {
      addToast(err.response?.data?.detail || 'Bulk import failed.', 'error');
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  const closeBulkModal = () => {
    setIsBulkOpen(false);
    setBulkText('');
    setBulkPreviewCount(null);
    setBulkParseError(null);
    setBulkResult(null);
  };

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
          Brain Training — GK/IQ Question Bank
          <span className="px-3 py-1 bg-[#1D9E75]/10 text-[#1D9E75] rounded-full text-sm font-semibold border border-[#1D9E75]/20">
            {total} Questions
          </span>
        </h1>
        <div className="flex items-center gap-3 sm:ml-auto">
          <button
            onClick={() => setIsBulkOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-bold transition-all shadow-sm active:scale-95"
          >
            📥 Bulk Import
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#1D9E75] hover:bg-[#15815f] text-white rounded-xl font-bold transition-all shadow-sm shadow-[#1D9E75]/20 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Question
          </button>
        </div>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Total</p>
            <p className="text-xl font-bold text-gray-800">{stats.total_questions}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Active</p>
            <p className="text-xl font-bold text-[#1D9E75]">{stats.active_questions}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">GK</p>
            <p className="text-xl font-bold text-blue-600">{stats.by_section?.gk ?? 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">IQ</p>
            <p className="text-xl font-bold text-purple-600">{stats.by_section?.iq ?? 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">By Difficulty</p>
            <p className="text-xs font-semibold text-gray-700 mt-1">
              🟢{stats.by_difficulty?.easy ?? 0} 🟡{stats.by_difficulty?.medium ?? 0} 🔴{stats.by_difficulty?.hard ?? 0}
            </p>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={sectionFilter}
          onChange={(e) => { setSectionFilter(e.target.value); resetToPage1(); }}
          className="bg-white border border-gray-200 text-sm font-medium rounded-xl px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] shadow-sm"
        >
          <option value="All">All Sections</option>
          <option value="gk">GK</option>
          <option value="iq">IQ</option>
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); resetToPage1(); }}
          className="bg-white border border-gray-200 text-sm font-medium rounded-xl px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] shadow-sm"
        >
          <option value="All">All Categories</option>
          <optgroup label="GK">
            {GK_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </optgroup>
          <optgroup label="IQ">
            {IQ_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </optgroup>
        </select>

        <select
          value={difficultyFilter}
          onChange={(e) => { setDifficultyFilter(e.target.value); resetToPage1(); }}
          className="bg-white border border-gray-200 text-sm font-medium rounded-xl px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] shadow-sm"
        >
          <option value="All">All Difficulties</option>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); resetToPage1(); }}
          className="bg-white border border-gray-200 text-sm font-medium rounded-xl px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] shadow-sm"
        >
          <option value="All">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-100">
              <tr>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-1/3">Question</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Section</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Difficulty</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Language</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Active</th>
                <th className="px-5 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 7 }).map((_, j) => <td key={j} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded w-full" /></td>)}
                  </tr>
                ))
              ) : questions.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-gray-400 font-medium text-base">No questions found.</td></tr>
              ) : (
                questions.map((q) => (
                  <tr key={q.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <p className="text-sm font-semibold text-gray-800 line-clamp-2">
                        {q.question_text_en.length > 80 ? q.question_text_en.substring(0, 80) + '...' : q.question_text_en}
                      </p>
                      {q.sub_category && <p className="text-xs text-gray-400 mt-0.5">{q.sub_category}</p>}
                    </td>
                    <td className="px-5 py-4"><SectionBadge section={q.section} /></td>
                    <td className="px-5 py-4 text-xs text-gray-600 font-medium">{CATEGORY_LABEL[q.category] || q.category}</td>
                    <td className="px-5 py-4"><DifficultyBadge difficulty={q.difficulty} /></td>
                    <td className="px-5 py-4 text-xs text-gray-500 uppercase">{q.language || 'both'}</td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => handleToggle(q)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${q.is_active ? 'bg-[#1D9E75]' : 'bg-gray-300'}`}
                        title={q.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${q.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(q)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#BA7517]/10 text-[#BA7517] hover:bg-[#BA7517]/20 border border-[#BA7517]/20 transition-all active:scale-95"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ open: true, id: q.id })}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#D85A30]/10 text-[#D85A30] hover:bg-[#D85A30]/20 border border-[#D85A30]/20 transition-all active:scale-95"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
            <span className="text-sm text-gray-500">
              Showing <span className="font-semibold text-gray-700">{(page - 1) * PER_PAGE + 1}</span>–
              <span className="font-semibold text-gray-700">{Math.min(page * PER_PAGE, total)}</span> of <span className="font-semibold text-gray-700">{total}</span>
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">Prev</button>
              <span className="text-sm px-3"><span className="font-bold text-[#085041]">{page}</span> / <span className="text-gray-500">{totalPages}</span></span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => !isSubmitting && setIsModalOpen(false)} title={editingId ? 'Edit Question' : 'Add New Question'} wide>
        <form onSubmit={handleFormSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Section</label>
              <select
                value={formData.section}
                onChange={(e) => handleSectionChange(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#1D9E75]"
              >
                <option value="gk">GK</option>
                <option value="iq">IQ</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#1D9E75]"
              >
                {categoriesForSection(formData.section).map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Difficulty</label>
              <select
                value={formData.difficulty}
                onChange={(e) => setFormData((p) => ({ ...p, difficulty: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#1D9E75]"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Language</label>
              <select
                value={formData.language}
                onChange={(e) => setFormData((p) => ({ ...p, language: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#1D9E75]"
              >
                <option value="both">Both</option>
                <option value="en">English only</option>
                <option value="si">Sinhala only</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Sub-category (optional)</label>
            <input
              type="text"
              value={formData.sub_category}
              onChange={(e) => setFormData((p) => ({ ...p, sub_category: e.target.value }))}
              placeholder="e.g. Colonial History"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-100 pt-5">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Question (English)</label>
              <textarea required rows={3} value={formData.question_text_en} onChange={(e) => setFormData((p) => ({ ...p, question_text_en: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] transition-all" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Question (Sinhala, optional)</label>
              <textarea rows={3} value={formData.question_text_si} onChange={(e) => setFormData((p) => ({ ...p, question_text_si: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] transition-all" />
            </div>
          </div>

          {(['a', 'b', 'c', 'd'] as const).map((letter) => (
            <div key={letter} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Option {letter.toUpperCase()} (English)</label>
                <input
                  required
                  type="text"
                  value={formData[`option_${letter}_en` as keyof FormState] as string}
                  onChange={(e) => setFormData((p) => ({ ...p, [`option_${letter}_en`]: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Option {letter.toUpperCase()} (Sinhala, optional)</label>
                <input
                  type="text"
                  value={formData[`option_${letter}_si` as keyof FormState] as string}
                  onChange={(e) => setFormData((p) => ({ ...p, [`option_${letter}_si`]: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all"
                />
              </div>
            </div>
          ))}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-gray-100 pt-5">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Correct Answer</label>
              <select
                value={formData.correct_option}
                onChange={(e) => setFormData((p) => ({ ...p, correct_option: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#1D9E75]"
              >
                <option value="a">Option A</option>
                <option value="b">Option B</option>
                <option value="c">Option C</option>
                <option value="d">Option D</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Explanation (English, optional)</label>
              <textarea rows={2} value={formData.explanation_en} onChange={(e) => setFormData((p) => ({ ...p, explanation_en: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] transition-all" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Explanation (Sinhala, optional)</label>
              <textarea rows={2} value={formData.explanation_si} onChange={(e) => setFormData((p) => ({ ...p, explanation_si: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] transition-all" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 mt-2">
            <button type="button" onClick={() => setIsModalOpen(false)} disabled={isSubmitting} className="px-6 py-2.5 rounded-xl text-gray-600 bg-gray-100 hover:bg-gray-200 font-semibold transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-xl text-white bg-[#085041] hover:bg-[#063d32] font-bold transition-all shadow-md shadow-[#085041]/20 active:scale-95 disabled:opacity-50 flex items-center justify-center min-w-[120px]">
              {isSubmitting ? (
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : editingId ? 'Save Changes' : 'Add Question'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal isOpen={isBulkOpen} onClose={() => !isBulkSubmitting && closeBulkModal()} title="Bulk Import Questions" wide>
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Paste a JSON array of question objects. Each object needs: <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">q_en, a_en, b_en, c_en, d_en, correct, section, category</code> at minimum
            (plus optional <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">q_si, a_si..d_si, exp_en, exp_si, sub_category, difficulty, language</code>).
          </p>
          <textarea
            rows={12}
            value={bulkText}
            onChange={(e) => { setBulkText(e.target.value); validateBulkText(e.target.value); }}
            placeholder='[{"q_en": "...", "a_en": "...", "b_en": "...", "c_en": "...", "d_en": "...", "correct": "a", "section": "gk", "category": "science", "difficulty": "medium"}]'
            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 font-mono text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] transition-all"
          />

          {bulkParseError && (
            <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
              {bulkParseError}
            </div>
          )}
          {bulkPreviewCount !== null && !bulkParseError && (
            <div className="px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm font-medium">
              Ready to import {bulkPreviewCount} question(s).
            </div>
          )}

          {bulkResult && (
            <div className="px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm space-y-2">
              <p className="font-semibold text-gray-800">
                ✅ {bulkResult.success_count} succeeded &nbsp;|&nbsp; ❌ {bulkResult.failed_count} failed
              </p>
              {bulkResult.errors.length > 0 && (
                <ul className="max-h-32 overflow-y-auto text-xs text-red-600 list-disc list-inside space-y-0.5">
                  {bulkResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={closeBulkModal} disabled={isBulkSubmitting} className="px-6 py-2.5 rounded-xl text-gray-600 bg-gray-100 hover:bg-gray-200 font-semibold transition-colors">
              Close
            </button>
            <button
              type="button"
              onClick={handleBulkSubmit}
              disabled={isBulkSubmitting || bulkPreviewCount === null || !!bulkParseError}
              className="px-6 py-2.5 rounded-xl text-white bg-[#1D9E75] hover:bg-[#15815f] font-bold transition-all shadow-md shadow-[#1D9E75]/20 active:scale-95 disabled:opacity-50 flex items-center justify-center min-w-[140px]"
            >
              {isBulkSubmitting ? 'Importing...' : 'Import Questions'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirms & Toasts */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Deactivate Question"
        message="This question will be deactivated and removed from the active pool. It can be reactivated later from the Inactive filter. Continue?"
        loading={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: '' })}
      />
      <ToastContainer toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </div>
  );
};

export default Brain;
